import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { ApiManager } from '../services/ApiManager';
import { EXPERIMENT_TOPICS } from '../constants/experiments';

interface SessionContextType {
  sessionId: string | null;
  loading: boolean;
  error: string | null;
  refetchSession: () => Promise<void>;
  // Experiments
  experiments: Record<string, string>;
  experimentsLoading: boolean;
  experimentsError: string | null;
  experimentsLoaded: boolean;
  getFormattedExperiments: () => string;
  // Test mode
  isTestMode: boolean;
  setTestMode: (testMode: boolean) => void;
}

const SessionContext = createContext<SessionContextType | undefined>(undefined);

interface SessionProviderProps {
  children: ReactNode;
  autoFetch?: boolean; // Controls whether to auto-fetch session on mount
}

const SESSION_STORAGE_KEY = 'playmerce_session_id';

export function SessionProvider({ children, autoFetch = true }: SessionProviderProps) {
  const [sessionId, setSessionId] = useState<string | null>(() => {
    // Initialize from sessionStorage if available
    try {
      return sessionStorage.getItem(SESSION_STORAGE_KEY);
    } catch (error) {
      console.warn('Failed to read session ID from sessionStorage:', error);
      return null;
    }
  });
  const [loading, setLoading] = useState(autoFetch);
  const [error, setError] = useState<string | null>(null);

  // Experiment state
  const [experiments, setExperiments] = useState<Record<string, string>>({});
  const [experimentsLoading, setExperimentsLoading] = useState(false);
  const [experimentsError, setExperimentsError] = useState<string | null>(null);
  const [experimentsLoaded, setExperimentsLoaded] = useState(false);

  // Test mode state
  const [isTestMode, setIsTestMode] = useState(false);

  const updateSessionId = (newSessionId: string | null) => {
    setSessionId(newSessionId);

    // Store in sessionStorage
    try {
      if (newSessionId) {
        sessionStorage.setItem(SESSION_STORAGE_KEY, newSessionId);
      } else {
        sessionStorage.removeItem(SESSION_STORAGE_KEY);
      }
    } catch (error) {
      console.warn('Failed to store session ID in sessionStorage:', error);
    }
  };

  const fetchSession = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await ApiManager.customer.getSession();
      const newSessionId = response.data.id;

      updateSessionId(newSessionId);

      // Also update the Analytics configuration with the session ID
      ApiManager.analytics.updateConfig({ sessionId: newSessionId });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch session';
      setError(errorMessage);
      console.error('Failed to fetch session:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchExperiments = async () => {
    if (!sessionId) return;

    setExperimentsLoading(true);
    setExperimentsError(null);

    try {
      const topics = Object.values(EXPERIMENT_TOPICS);
      const assignments: Record<string, string> = {};

      // Fetch experiment assignments for all topics in parallel
      const promises = topics.map(async (topic) => {
        try {
          const response = await ApiManager.customer.getExperiment(topic, sessionId);
          assignments[response.data.topic] = response.data.group;
        } catch (error) {
          console.warn(`Failed to fetch experiment for topic ${topic}:`, error);
          // Don't include failed topics in assignments
        }
      });

      await Promise.allSettled(promises);

      setExperiments(assignments);
      setExperimentsLoaded(true);
    } catch (error) {
      setExperimentsError(error instanceof Error ? error.message : 'Failed to fetch experiments');
    } finally {
      setExperimentsLoading(false);
    }
  };

  const getFormattedExperiments = () => {
    const experimentEntries = Object.entries(experiments);

    // Add test mode experiment if in test mode
    if (isTestMode) {
      experimentEntries.push(['mode', 'test']);
    }

    return experimentEntries.map(([topic, group]) => `${topic}:${group}`).join(',');
  };

  const setTestMode = (testMode: boolean) => {
    setIsTestMode(testMode);
  };

  const refetchSession = async () => {
    await fetchSession();
  };

  // Fetch session when component mounts
  useEffect(() => {
    // If we have a stored session ID, configure analytics immediately
    if (sessionId) {
      ApiManager.analytics.updateConfig({ sessionId });
      setLoading(false);
    } else if (autoFetch) {
      // No stored session and auto-fetch is enabled, fetch a new one
      fetchSession();
    } else {
      // Auto-fetch is disabled, don't fetch session
      setLoading(false);
    }
  }, [autoFetch]); // Include autoFetch in dependency array

  // Fetch experiments when session is available
  useEffect(() => {
    if (sessionId && !experimentsLoaded && !experimentsLoading) {
      fetchExperiments();
    }
  }, [sessionId, experimentsLoaded, experimentsLoading]);

  const value: SessionContextType = {
    sessionId,
    loading,
    error,
    refetchSession,
    experiments,
    experimentsLoading,
    experimentsError,
    experimentsLoaded,
    getFormattedExperiments,
    isTestMode,
    setTestMode,
  };

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextType {
  const context = useContext(SessionContext);
  if (context === undefined) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
}
